import React from 'react';
import { Box, Button, Typography, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDropzone } from 'react-dropzone';

const UploadBox = styled(Paper)(({ theme }) => ({
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  textAlign: 'center',
  cursor: 'pointer',
  backgroundColor: theme.palette.background.default,
  transition: 'border-color 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '200px',
}));

const VideoPreviewContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

const VideoPreview = styled('video')({
  maxWidth: '100%',
  maxHeight: '300px',
  objectFit: 'contain',
  borderRadius: '4px',
});

const DeleteButton = styled(Button)(({ theme }) => ({
  position: 'absolute',
  top: '8px',
  right: '8px',
  minWidth: 'unset',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
}));

export interface UploadedVideo {
  file: File;
  preview: string;
}

export interface VideoUploaderProps {
  uploadedVideo: UploadedVideo | null;
  onUpload: (acceptedFiles: File[]) => void;
  onRemove: () => void;
}

const VideoUploader: React.FC<VideoUploaderProps> = ({ uploadedVideo, onUpload, onRemove }) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'video/*': ['.mp4', '.mov', '.avi', '.mkv', '.webm'] },
    maxFiles: 1,
    onDrop: onUpload,
  });

  return (
    <>
      {uploadedVideo ? (
        <VideoPreviewContainer>
          <VideoPreview src={uploadedVideo.preview} controls />
          <DeleteButton
            variant="contained"
            color="error"
            size="small"
            onClick={onRemove}
            startIcon={<DeleteIcon />}
          >
            Remove
          </DeleteButton>
        </VideoPreviewContainer>
      ) : (
        <UploadBox {...getRootProps()}>
          <input {...getInputProps()} />
          <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {isDragActive ? 'Drop the video here' : 'Drag & drop a video here'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            or click to select a file
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Supported formats: MP4, MOV, AVI, MKV, WebM
          </Typography>
        </UploadBox>
      )}
    </>
  );
};

export default VideoUploader;
